import { observer } from 'mobx-react-lite'
import { Outlet, useLoaderData, useNavigate } from 'react-router'
import { useActivation } from '@/hooks/view-model-hooks'
import type { JobDetailsViewModel } from '@/features/jobs/details/JobDetailsViewModel'
import { TaxJobDetailsViewModel } from '@/features/jobs/details/tax-type/TaxJobDetailsViewModel'
import { TaxJobDetailsHeader } from '@/features/jobs/details/tax-type/components/TaxJobDetailsHeader'
import { Page } from '@/features/jobs/details/components/PageLayout'
import { useDocumentTitle } from '@/hooks/document-title'
import { useProvideViewportBackground } from '@/features/shell/global-navigation/GlobalNavigationContainer'
import { createContext, useContext, useMemo } from 'react'
import {
  useDefaultHeaderContent,
  getPendingJobScopeChangeElements,
  getRatingsElements,
  PageHeader,
} from '@/features/jobs/details/components/PageHeader'
import { ConsultationJobDetailsViewModel } from '@/features/jobs/details/consultation-type/ConsultationJobDetailsViewModel'
import { AvailabilitySubmittedDialogContainer } from '@/features/jobs/details/consultation/components/AvailabilitySubmittedDialog'
import { ConfirmAvailabilityDialogContainer } from '@/features/jobs/details/consultation/components/ConfirmAvailabilityDialog'
import { SetAvailabilityDialogContainer } from '@/features/jobs/details/consultation/components/SetAvailabilityDialog'
import { ReviewAvailabilityDialogContainer } from '@/features/jobs/details/consultation/components/ReviewAvailabilityDialog'
import { ConsultationJobDetailsHeader } from '@/features/jobs/details/consultation-type/components/ConsultationJobDetailsHeader'
import { BookkeepingJobDetailsViewModel } from '@/features/jobs/details/bookkeeping-type/BookkeepingJobDetailsViewModel'
import { BookkeepingJobDetailsHeader } from '@/features/jobs/details/bookkeeping-type/components/BookkeepingJobDetailsHeader'
import { BookkeepingConfirmationAccessDialogContainer } from '@/features/jobs/details/bookkeeping-type/components/BookkeepingConfirmAccessDialog'
import { BookkeepingPlatformSelectionDialogContainer } from '@/features/bookkeeping/components/BookkeepingPlatformSelectionDialog'
import { JobRatingsDialogsContainer } from '@/features/jobs/details/ratings/components/SubmitRatingsDialog'
import { JobScopeChangeDialogContainer } from '@/features/jobs/details/components/JobScopeChangeDialog/JobScopeChangeDialogContainer'
import { links } from '@/utils/link'
import { useObservable } from '@/hooks/rx-hooks'
import { EMPTY } from 'rxjs'

/**
 * The JobDetailsView
 */
export default observer(function JobDetailsView() {
  // The route loader returns the loaded job.
  const { vm } = useLoaderData<{ vm: JobDetailsViewModel }>()

  useActivation(vm)
  useDocumentTitle(vm.job.title)
  useProvideViewportBackground('secondary')

  return (
    <>
      <Page testId="job-details-page">
        <HeaderContainer vm={vm} />
        <JobDetailsViewModelContext value={vm}>
          <Outlet />
        </JobDetailsViewModelContext>
        <JobDetailsDialogsHostContainer vm={vm} />
      </Page>
    </>
  )
})

interface HeaderContainerProps {
  vm: JobDetailsViewModel
}

/**
 * Header Container
 */
export const HeaderContainer = observer(function HeaderContainer({
  vm,
}: HeaderContainerProps) {
  if (vm.typeVm instanceof TaxJobDetailsViewModel) {
    return <TaxJobDetailsHeader taxVm={vm.typeVm} jobVm={vm} />
  }

  if (vm.typeVm instanceof ConsultationJobDetailsViewModel) {
    return (
      <ConsultationJobDetailsHeader consultationVm={vm.typeVm} jobVm={vm} />
    )
  }

  if (vm.typeVm instanceof BookkeepingJobDetailsViewModel) {
    return (
      <BookkeepingJobDetailsHeader bookkeepingJobVm={vm.typeVm} jobVm={vm} />
    )
  }

  return <DefaultJobDetailsHeader vm={vm} />
})

/**
 * Default job details header when no customizations are needed for the job type.
 */
const DefaultJobDetailsHeader = observer(function DefaultJobDetailsHeader({
  vm,
}: HeaderContainerProps) {
  const { title, description } = useDefaultHeaderContent(vm)
  const actionElements =
    getPendingJobScopeChangeElements(vm) ?? getRatingsElements(vm)

  return (
    <PageHeader
      vm={vm}
      headline={title}
      description={description}
      scrolledHeadline={title}
      actionCard={actionElements?.actionCard}
      scrolledAction={actionElements?.scrolledAction}
    />
  )
})

/**
 * Hosts dialogs for the job details view.
 */
const JobDetailsDialogsHostContainer = observer(
  function JobDetailsDialogsHostContainer({ vm }: { vm: JobDetailsViewModel }) {
    const navigate = useNavigate()
    const managerVm = getConsultationManagerVm(vm)
    const schedulerVm = managerVm?.consultationSchedulerVm

    const availabilitySubmittedClose$ = useMemo(
      () => schedulerVm?.availabilitySubmittedDialog.close$ ?? EMPTY,
      [schedulerVm],
    )

    useObservable(availabilitySubmittedClose$, () => {
      if (vm.conversationVm.unreadCount > 0) {
        void navigate(links.jobDetailsMessages(vm.job.id))
      }
    })

    return (
      <>
        <JobScopeChangeDialogContainer
          vm={vm.jobScopeChangesDisplayVm.modalJobScopeChangeVm}
        />
        <JobRatingsDialogsContainer vm={vm.ratingsVm} />
        {schedulerVm && managerVm.consultation && (
          <>
            <ConfirmAvailabilityDialogContainer vm={schedulerVm} />
            <SetAvailabilityDialogContainer vm={schedulerVm} />
            <AvailabilitySubmittedDialogContainer vm={schedulerVm} />
            <ReviewAvailabilityDialogContainer vm={schedulerVm} />
          </>
        )}
        {vm.typeVm instanceof BookkeepingJobDetailsViewModel && (
          <>
            <BookkeepingPlatformSelectionDialogContainer
              vm={vm.typeVm.bookkeepingVm.bookkeepingPlatformSelectionDialogVm}
            />

            <BookkeepingConfirmationAccessDialogContainer
              vm={vm.typeVm.bookkeepingVm.bookkeepingConfirmAccessDialogVm}
            />
          </>
        )}
      </>
    )
  },
)

/**
 * Context for providing the job details view model instance to child routes.
 */
const JobDetailsViewModelContext = createContext<JobDetailsViewModel>(null!)

/**
 * Gets the job details view model instance from the context.
 */
export function useJobDetailsViewModel() {
  return useContext(JobDetailsViewModelContext)
}

function getConsultationManagerVm(vm: JobDetailsViewModel) {
  if (
    vm.typeVm instanceof TaxJobDetailsViewModel ||
    vm.typeVm instanceof ConsultationJobDetailsViewModel
  ) {
    return vm.typeVm.consultationManagerVm
  }

  return null
}
